import { useCallback, useMemo, useReducer } from 'react';

type SortingState = '-date';

type PaginationState = {
  cursorPrev: number | null;
  cursorNext: number | null;
};

export type LocalState = PaginationState & { sort: SortingState };

export type Action =
  | { type: 'reset' }
  | { type: 'setSort'; value: string }
  | { type: 'setCursorPrev'; value: number | null }
  | { type: 'setCursorNext'; value: number | null };

function reducer(state: LocalState, action: Action): LocalState {
  switch (action.type) {
    case 'setSort':
      return {
        ...state,
        sort: (action.value as SortingState) || '-date'
      };
    case 'setCursorPrev':
      return {
        ...state,
        cursorPrev: action.value,
        cursorNext: null
      };
    case 'setCursorNext':
      return {
        ...state,
        cursorPrev: null,
        cursorNext: action.value
      };
    case 'reset':
      return {
        sort: '-date',
        cursorPrev: null,
        cursorNext: null
      };
  }
}

const reset = (): Action => ({ type: 'reset' });
const setSort = (value: string): Action => ({ type: 'setSort', value });
const setCursorPrev = (value: number | null): Action => ({
  type: 'setCursorPrev',
  value
});
const setCursorNext = (value: number | null): Action => ({
  type: 'setCursorNext',
  value
});

export function useLocalState() {
  const initialState: LocalState = useMemo<LocalState>(
    () => ({
      sort: '-date',
      cursorPrev: null,
      cursorNext: null
    }),
    []
  );

  const [state, dispatch] = useReducer(reducer, initialState);

  const updateCursorPrev = useCallback(
    (value: number | null) => {
      return dispatch(setCursorPrev(value));
    },
    [dispatch]
  );

  const queryParams = useMemo(() => {
    return {
      sort: state.sort,
      cursorPrev: state.cursorPrev,
      cursorNext: state.cursorNext
    };
  }, [state]);

  return {
    state,
    queryParams,
    dispatch,
    reset,
    setSort,
    setCursorNext,
    setCursorPrev,
    updateCursorPrev
  };
}
